import { Avatar, Badge, Box, Flex, Group, Menu, MenuTarget, ScrollArea, Stack, Text, useComputedColorScheme } from "@mantine/core"
import cx from "clsx"
import classes from "./UserButton.module.scss"
import { IconChevronDown, IconExternalLink, IconLoader2, IconLogout, IconShieldLock, IconUser, IconUserShield } from "@tabler/icons-react"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { useMsal } from "@azure/msal-react"
import { MsalAuthService } from "@features/auth/services/auth.service.ts"
import { useImpersonateExternal, useLogout } from "@features/auth/hooks/auth.queries.ts"

export type TUserButton = {
  name?: string
  withLogout?: boolean
  baseClass?: boolean
  className?: string
}

const UserButton = ({ name, withLogout = true, baseClass = true, className }: TUserButton) => {
  const { user, hasRole, logout } = useAuth()
  const { instance } = useMsal()
  const computedColorScheme = useComputedColorScheme("light", { getInitialValueInEffect: true })
  const { mutateAsync: logoutMutationAsync, isPending: isLogoutAsyncPending } = useLogout()
  const { mutateAsync: impersonateExternal, isPending: isImpersonating } = useImpersonateExternal()

  const displayName = name || (user ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.username : "Usuario")

  const formatName = () => {
    const nameArr = displayName?.split(" ")
    if (nameArr?.length === 1) return displayName
    if (displayName?.length > 40) {
      return `${nameArr[0]} ${nameArr[1][0]}.`
    }
    return displayName
  }

  const newName = formatName()
  const isLoggedInWithMicrosoft = !!user?.tenantId
  const isAdmin = hasRole("admin")

  return (
    <Group justify="space-between" className={cx({ [classes.user]: baseClass }, className)}>
      <Menu position="bottom-start" shadow="md">
        <MenuTarget>
          <Flex align="center" justify="space-between" style={{ cursor: "pointer" }} gap="xs" w="100%">
            <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <Group gap={6} wrap="nowrap" justify="space-between">
                <Avatar name={`${user?.firstName} ${user?.lastName}`} size={32} radius="xl" color="initials" allowedInitialsColors={computedColorScheme === "light" ? ["biocells-green.0"] : ["biocells-green.7"]} />
                <Text size="sm" fw={500} style={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>
                  {newName}
                </Text>
              </Group>
            </div>
            <IconChevronDown size={14} stroke={1.5} />
          </Flex>
        </MenuTarget>

        <Menu.Dropdown>
          <Stack gap={2}>
            <Menu.Label>Información de usuario</Menu.Label>
            <Menu.Item leftSection={isAdmin ? <IconUserShield size={14} /> : <IconUser size={14} />}>
              <Flex gap="xs" align="center">
                Email:
                <Text size="xs" fw={500}>
                  {user?.email}
                </Text>
              </Flex>
            </Menu.Item>
            <Menu.Item leftSection={<IconUser size={14} />}>
              <Flex gap="xs" align="center">
                Inicio de sesión:
                <Badge color={isLoggedInWithMicrosoft ? "violet" : "orange"} variant="filled" size="xs" style={{ minWidth: 72, textAlign: "center" }} className={classes.loginBadge}>
                  {isLoggedInWithMicrosoft ? "Microsoft" : "Sistema"}
                </Badge>
              </Flex>
            </Menu.Item>

            <Menu.Divider />
            <Menu.Label>Roles ({user?.roles?.length || 0})</Menu.Label>
            <Box px="sm" pb="xs">
              <Group gap={4}>
                {user?.roles?.map((role) => (
                  <Badge key={role} size="xs" variant="light" color="blue">
                    {role}
                  </Badge>
                ))}
              </Group>
            </Box>

            {user?.permissions && user.permissions.length > 0 && (
              <>
                <Menu.Divider />
                <Menu.Label>Permisos ({user.permissions.length})</Menu.Label>
                <ScrollArea.Autosize mah={200} px="sm" pb="xs">
                  <Stack gap={4}>
                    {user.permissions.map((p, index) => (
                      <Group key={index} gap={6} wrap="nowrap">
                        <IconShieldLock size={12} style={{ flexShrink: 0 }} />
                        <Text size="xs">
                          <Text component="span" fw={700} inherit>
                            {p.action}
                          </Text>{" "}
                          en {p.subject}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              </>
            )}
          </Stack>

          {isAdmin && (
            <>
              <Menu.Divider />
              <Menu.Label>Administración</Menu.Label>
              <Menu.Item leftSection={isImpersonating ? <IconLoader2 size={14} className="animate-spin" /> : <IconExternalLink size={14} />} onClick={() => impersonateExternal()}>
                Loguearme como Fausto
              </Menu.Item>
            </>
          )}

          {withLogout && (
            <Menu.Item
              className={classes.logoutMenuItem}
              leftSection={isLogoutAsyncPending ? <IconLoader2 /> : <IconLogout size={14} />}
              onClick={async () => {
                const isLocalLogout = !user?.tenantId || hasRole("provider")
                await MsalAuthService.handleLogout({ logout, logoutMutationAsync, instance, isLocalLogout })
              }}
            >
              <Menu.Divider />
              Cerrar sesión
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  )
}

export default UserButton
